import React, { useCallback, useContext, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';

import Axios from '../../Assets/Axios/Axios';

import ToastContext from '../../Context/ToastContext';

import Layout from './Components/Layout';

import { toCurrencyString } from '../../Helpers';

import { ProfessionalServices } from '../../Server/Query';
import {
  ProCreateServicePackage,
  ProUpdateServicePackage,
  ProDeleteServicePackage,
} from '../../Server/Mutation';

const EMPTY_PACKAGE = {
  id: null,
  serviceId: null,
  name: '',
  price: '',
  features: '',
  description: '',
  isActive: true,
};

const Packages = () => {
  const { showToastMessage } = useContext(ToastContext);

  const [services, setServices] = useState([]);
  const [packageForm, setPackageForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchServices = useCallback(async () => {
    const { data, errors } = await Axios(ProfessionalServices);
    if (errors || !data?.professionalServices) {
      showToastMessage('Failed to load your packages.', 'error');
      return;
    }
    setServices(data.professionalServices);
  }, [showToastMessage]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  async function handleSavePackage() {
    if (!packageForm.name.trim() || packageForm.price === '') {
      showToastMessage('Package name and price are required.', 'error');
      return;
    }
    const features = packageForm.features
      .split(',')
      .map(f => f.trim())
      .filter(Boolean);

    setBusy(true);
    try {
      const payload = {
        name: packageForm.name.trim(),
        price: packageForm.price,
        description: packageForm.description || null,
        features,
        isActive: packageForm.isActive,
      };
      const { errors } = await Axios(
        packageForm.id
          ? ProUpdateServicePackage({ id: packageForm.id, ...payload })
          : ProCreateServicePackage({ serviceId: packageForm.serviceId, ...payload })
      );
      if (errors) {
        showToastMessage(errors[0]?.message ?? 'Failed to save package.', 'error');
        return;
      }
      showToastMessage(`Package ${packageForm.id ? 'updated' : 'added'}.`, 'success');
      setPackageForm(null);
      await fetchServices();
    } finally {
      setBusy(false);
    }
  }

  async function handleDeletePackage() {
    const target = deleteTarget;
    setDeleteTarget(null);
    const { errors } = await Axios(ProDeleteServicePackage({ id: target.id }));
    if (errors) {
      showToastMessage(errors[0]?.message ?? 'Failed to delete package.', 'error');
      return;
    }
    showToastMessage('Package deleted.', 'success');
    await fetchServices();
  }

  return (
    <Layout message='Update your Package Details' isAvatarButtonsAvailable={false}>
      <Box maxWidth={760}>
        <Typography variant='h6' fontWeight={600} mb={2}>
          Packages by service
        </Typography>

        {services.length === 0 && (
          <Typography color='text.secondary'>
            You don&apos;t have any services yet. Add a service first, then its packages.
          </Typography>
        )}

        <Stack spacing={3}>
          {services.map(service => (
            <Paper key={service.id} variant='outlined' sx={{ p: 2 }}>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Box>
                  <Chip
                    size='small'
                    label={service.category?.name ?? 'Uncategorised'}
                    color='primary'
                    variant='outlined'
                    sx={{ mb: 0.5 }}
                  />
                  <Typography variant='subtitle1' fontWeight={600}>
                    {service.name}
                  </Typography>
                </Box>
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() => setPackageForm({ ...EMPTY_PACKAGE, serviceId: service.id })}
                >
                  Add package
                </Button>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              {service.packages.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  No packages for this service yet.
                </Typography>
              ) : (
                <Box display='grid' gridTemplateColumns={{ xs: 'auto', md: 'repeat(2,1fr)' }} gap={2}>
                  {service.packages.map(pkg => (
                    <Box key={pkg.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.50' }}>
                      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                        <Box>
                          <Typography fontWeight={600}>
                            {pkg.name} — {toCurrencyString(pkg.price)}
                            {!pkg.isActive && <Chip label='Inactive' size='small' sx={{ ml: 1 }} />}
                          </Typography>
                          {pkg.description && (
                            <Typography variant='body2' color='text.secondary'>
                              {pkg.description}
                            </Typography>
                          )}
                          {(pkg.features ?? []).length > 0 && (
                            <Stack spacing={0.25} mt={0.5}>
                              {pkg.features.map((f, i) => (
                                <Stack key={i} direction='row' alignItems='center' gap={0.5}>
                                  <CheckCircleIcon fontSize='small' color='success' />
                                  <Typography variant='body2'>{f}</Typography>
                                </Stack>
                              ))}
                            </Stack>
                          )}
                        </Box>
                        <Box>
                          <IconButton
                            size='small'
                            onClick={() =>
                              setPackageForm({
                                id: pkg.id,
                                serviceId: service.id,
                                name: pkg.name,
                                price: pkg.price,
                                features: (pkg.features ?? []).join(', '),
                                description: pkg.description ?? '',
                                isActive: pkg.isActive,
                              })
                            }
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                          <IconButton size='small' color='error' onClick={() => setDeleteTarget(pkg)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          ))}
        </Stack>
      </Box>

      {/* Add / edit package */}
      <Dialog open={Boolean(packageForm)} onClose={() => setPackageForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{packageForm?.id ? 'Edit Package' : 'Add Package'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label='Name'
              required
              placeholder='e.g. Standard, Premium'
              value={packageForm?.name ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label='Price'
              type='number'
              required
              value={packageForm?.price ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, price: e.target.value }))}
            />
            <TextField
              label='Features'
              placeholder='Comma-separated'
              helperText='Separate each feature with a comma.'
              value={packageForm?.features ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, features: e.target.value }))}
            />
            <TextField
              label='Description'
              multiline
              minRows={2}
              value={packageForm?.description ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, description: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={packageForm?.isActive ?? true}
                  onChange={e => setPackageForm(f => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label='Active'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageForm(null)}>Cancel</Button>
          <Button variant='contained' onClick={handleSavePackage} disabled={busy}>
            {packageForm?.id ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete package</DialogTitle>
        <DialogContent>
          <Typography>Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color='error' variant='contained' onClick={handleDeletePackage}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Packages;
